/**
 * The release registry of the SFE (`release/sfe-registry.json`) as parsed
 * data: every version whose attestation is recorded, with the DGI
 * decisions on it, each version's fingerprint and the oldest version it
 * syncs with, the decisions on component versions (`componentDecisions`)
 * and the « pin to version X » instructions (`pins`).
 *
 * A registry that does not parse, has no version, or carries a malformed
 * attestation or decision reads as unknown. Its `pins` are read apart:
 * pins that do not read leave every other entry readable.
 *
 * Pure: the caller reads the file (or the content a bundle inlines).
 */
import { parseSfeAttestationDecisions, parseSfeComponentDecisions } from "./sfe-attestation-state.js";
import { parseSfePins } from "./sfe-pin.js";
import { parseSfeValidatedScope } from "./sfe-runtime-proof.js";
const DAY = /^\d{4}-\d{2}-\d{2}$/;
/** The state of a registry that cannot be read. */
export const SFE_REGISTRY_UNKNOWN = Object.freeze({
    known: false,
    attestations: Object.freeze([]),
    versions: Object.freeze([]),
    componentDecisions: Object.freeze([]),
});
/** The attestations, versions and component decisions of the registry, from its parsed content. */
export function sfeAttestationOf(registry) {
    const versions = registry?.versions;
    if (!Array.isArray(versions) || versions.length === 0)
        return SFE_REGISTRY_UNKNOWN;
    const componentDecisions = parseSfeComponentDecisions(registry.componentDecisions);
    if (componentDecisions === null)
        return SFE_REGISTRY_UNKNOWN;
    const read = [];
    for (const entry of versions) {
        const candidate = entry;
        if (candidate === null || typeof candidate !== "object" || typeof candidate.version !== "string") {
            return SFE_REGISTRY_UNKNOWN;
        }
        const attestation = candidate.attestation;
        if (attestation === undefined || attestation === null || typeof attestation !== "object") {
            return SFE_REGISTRY_UNKNOWN;
        }
        const decisions = parseSfeAttestationDecisions(attestation.decisions);
        if (decisions === null)
            return SFE_REGISTRY_UNKNOWN;
        const fingerprint = typeof candidate.fingerprint === "string" ? candidate.fingerprint : null;
        const sync = typeof candidate.syncCompatibleFrom === "string" ? { syncCompatibleFrom: candidate.syncCompatibleFrom } : {};
        const validatedScope = parseSfeValidatedScope(candidate.validatedScope);
        const scope = validatedScope === null ? {} : { validatedScope };
        if (attestation.number === null) {
            if (decisions.length > 0)
                return SFE_REGISTRY_UNKNOWN;
            read.push({ version: candidate.version, fingerprint, attestation: null, ...sync, ...scope });
            continue;
        }
        if (typeof attestation.number !== "string" || attestation.number.trim() === "")
            return SFE_REGISTRY_UNKNOWN;
        if (typeof attestation.date !== "string" || !DAY.test(attestation.date))
            return SFE_REGISTRY_UNKNOWN;
        const isf = typeof attestation.isf === "string" ? attestation.isf.trim() : "";
        read.push({
            version: candidate.version,
            fingerprint,
            attestation: { number: attestation.number, date: attestation.date, decisions },
            ...sync,
            ...scope,
            ...(isf === "" ? {} : { isf }),
        });
    }
    const pins = parseSfePins(registry.pins);
    return {
        known: true,
        attestations: read.flatMap((entry) => (entry.attestation ? [entry.attestation] : [])),
        versions: read,
        componentDecisions,
        pins: pins === null || pins.malformed > 0 ? null : pins.pins,
    };
}
/** The state of the registry whose JSON text is `content`; unknown when it does not parse. */
export function sfeAttestationOfContent(content) {
    try {
        return sfeAttestationOf(JSON.parse(content));
    }
    catch {
        return SFE_REGISTRY_UNKNOWN;
    }
}
//# sourceMappingURL=sfe-registry.js.map